import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { IconButton, MenuItem, MenuList } from '@material-ui/core';
import { createStyles, Theme, WithStyles, withStyles } from '@material-ui/core/styles';
import { ImportContacts as ImportContactsIcon, KeyboardArrowDown as KeyboardArrowDownIcon } from '@material-ui/icons';
import classnames from 'classnames';

import MenuContainer from 'embeds/components/MenuContainer';
import { Button, ButtonProps, Tooltip, Typography } from 'embeds/components';

const styles = ({
  custom: {
    palette: { colorGrayishBlue },
    fontSizes,
  },
  palette: {
    common: { white },
    primary,
  },
  spacing: { unit },
}: Theme) =>
  createStyles({
    button: {
      backgroundColor: `${white} !important`,
      border: `1px solid ${colorGrayishBlue}`,
      borderRadius: unit / 2,
      height: 40,
    },
    buttonIcon: {
      color: primary.main,
      fontSize: fontSizes.body,
    },
    buttonLabel: {
      color: primary.main,
      fontWeight: 500,
      padding: `0px ${unit}px`,
    },
    iconButton: {
      color: primary.main,
      width: unit * 6,
    },
    hidden: {
      display: 'none !important',
    },
  });

const messages = defineMessages({
  label: {
    id: 'components.SavedTemplateButton.label',
    defaultMessage: 'Select message from library',
  },
  tooltip: {
    id: 'components.SavedTemplateButton.tooltip',
    defaultMessage: 'Message Library',
  },
  actionChange: {
    id: 'components.SavedTemplateButton.actionChange',
    defaultMessage: 'Change message',
  },
  actionClear: {
    id: 'components.SavedTemplateButton.actionClear',
    defaultMessage: 'Clear',
  },
});

export interface SavedTemplateButtonProps extends WithStyles<typeof styles>, Omit<ButtonProps, 'classes'> {
  className?: string;
  disabled?: boolean;
  hide?: boolean;
  iconButtonMode?: boolean;
  onClearRequest?: () => void;
  selectedTemplateMessage: boolean | any;
}

const SavedTemplateButton: React.FC<SavedTemplateButtonProps> = ({
  classes,
  className,
  disabled,
  hide,
  iconButtonMode,
  selectedTemplateMessage,
  onClearRequest,
  onClick = () => {},
  ...rest
}) => {
  const { formatMessage } = useIntl();
  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);

  const handleButtonClick = (event?: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (event && !selectedTemplateMessage) {
      onClick(event);
    } else if (!event || menuAnchor) {
      setMenuAnchor(null);
    } else if (!menuAnchor) {
      setMenuAnchor(event.currentTarget);
    }
  };

  const handleMenuItemClick = (event: React.MouseEvent<HTMLElement, MouseEvent>, action: 'change' | 'clear') => {
    if (action === 'clear') {
      if (onClearRequest) onClearRequest();
    } else {
      onClick(event);
    }
    handleButtonClick();
  };

  const label = selectedTemplateMessage ? selectedTemplateMessage.name : formatMessage(messages.label);

  return (
    <>
      {iconButtonMode ? (
        <Tooltip open={tooltipOpen} title={formatMessage(messages.tooltip)} hideArrow placement="top">
          <IconButton
            data-lh-id="SavedTemplateButton-btn-icon"
            type="button"
            aria-label={label}
            className={classnames(classes.iconButton, className, { [classes.hidden]: hide })}
            disabled={disabled || false}
            onClick={(event) => {
              setTooltipOpen(false);
              handleButtonClick(event);
            }}
            onMouseEnter={() => setTooltipOpen(true)}
            onMouseLeave={() => setTooltipOpen(false)}
          >
            <ImportContactsIcon className={classes.buttonIcon} />
          </IconButton>
        </Tooltip>
      ) : (
        <Button
          data-lh-id="SavedTemplateButton-btn"
          type="button"
          aria-label={label}
          className={classnames(classes.button, className, { [classes.hidden]: hide })}
          disabled={disabled}
          disableFocusRipple
          disableRipple
          onClick={handleButtonClick}
          variant="outlined"
          {...rest}
        >
          <ImportContactsIcon className={classes.buttonIcon} />
          <Typography className={classes.buttonLabel}>{label}</Typography>
          <KeyboardArrowDownIcon
            className={classnames(classes.buttonIcon, { [classes.hidden]: !selectedTemplateMessage })}
          />
        </Button>
      )}
      <MenuContainer menuAnchor={menuAnchor} onClickAway={handleButtonClick} placement="top-start">
        <MenuList disablePadding>
          <MenuItem onClick={(event) => handleMenuItemClick(event, 'change')}>
            <Typography component="span" variant="subtitle1">
              {formatMessage(messages.actionChange)}
            </Typography>
          </MenuItem>
          <MenuItem onClick={(event) => handleMenuItemClick(event, 'clear')}>
            <Typography component="span" variant="subtitle1">
              {formatMessage(messages.actionClear)}
            </Typography>
          </MenuItem>
        </MenuList>
      </MenuContainer>
    </>
  );
};

export default withStyles(styles)(SavedTemplateButton);
